import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock3, Search, BusFront } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { sourceLabel } from "@/components/StopTimeline";
import { api } from "@/lib/api";
import { fmtTime } from "@/lib/format";

export function usePublicLive(stationId) {
  const [buses, setBuses] = useState(null);
  const load = useCallback(() => api.get("/public/tracking/live", { params: stationId ? { station_id: stationId } : {} })
    .then((r) => setBuses(r.data)).catch(() => setBuses((b) => b || [])), [stationId]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  return buses;
}

export const NextStop = ({ b }) => (b.next_stop
  ? <>Next: <b>{b.next_stop.city}</b> ~{fmtTime(b.next_stop.eta)}</>
  : <>At <b>{b.timeline[b.timeline.length - 1].city}</b></>);

const DelayChip = ({ b }) => (b.delay_min > 5
  ? <span className="chip bg-rose-50 text-rose-700"><Clock3 className="h-3 w-3" />{b.delay_min} min late</span>
  : <span className="chip bg-emerald-50 text-emerald-700">On time</span>);

export function PublicBusPopup({ b }) {
  return (
    <div className="space-y-1.5 font-sans" data-testid={`public-bus-popup-${b.trip_id}`}>
      <div className="font-display text-base font-extrabold text-[#182678]">{b.route}</div>
      <div className="text-xs text-slate-500">Bus {b.bus_number} · {b.bus_type} · {b.status === "departed" ? "left" : "departs"} {fmtTime(b.departure)}</div>
      <div className="text-xs text-slate-700"><NextStop b={b} /></div>
      <div className="flex items-center justify-between gap-2"><DelayChip b={b} /><span className="text-[10px] text-slate-400">{sourceLabel(b.position)}</span></div>
    </div>
  );
}

export function LiveBusCard({ b, selected, onPick }) {
  return (
    <button onClick={() => onPick(b)} data-testid={`live-bus-card-${b.trip_id}`}
      className={`btn-press w-full rounded-2xl border p-3 text-left transition-colors ${selected ? "border-[#0052FF] bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300"}`}>
      <div className="flex items-center justify-between gap-2"><span className="font-display text-sm font-bold">{b.route}</span><DelayChip b={b} /></div>
      <div className="mt-0.5 text-xs text-slate-500">Bus <span className="font-mono">{b.bus_number}</span> · <NextStop b={b} /></div>
    </button>
  );
}

export function TrackCodeBox({ compact }) {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (code.trim()) nav(`/track/${code.trim().toUpperCase()}`); }}
      className="rounded-2xl bg-[#182678] p-4 text-white" data-testid="track-code-box">
      {!compact && <div className="mb-2 flex items-center gap-2 font-display font-bold"><BusFront className="h-4 w-4 text-[#FCD116]" />Track my bus</div>}
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Booking code" className="h-10 border-0 bg-white font-mono font-bold uppercase text-slate-900" data-testid="track-code-box-input" />
        <Button type="submit" className="h-10 rounded-xl bg-[#FCD116] px-4 font-bold text-[#182678] hover:bg-yellow-300" data-testid="track-code-box-submit"><Search className="h-4 w-4" />Track</Button>
      </div>
    </form>
  );
}
